import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRelativeFine } from '@/lib/formatRelativeDate'
import type { Document } from '@/types/api'

interface SourceItemProps {
  document: Document
}

const TYPE_LABEL: Record<Document['type'], string> = {
  pdf: 'PDF',
  audio: 'MP3',
  text: 'TXT',
  url: 'URL',
  pptx: 'PPTX',
}

const STATUS_TONE: Record<
  Document['status'],
  { label: string; tone: string; Icon: typeof CheckCircle2 | null }
> = {
  uploading: { label: 'Uploading', tone: 'text-text-secondary', Icon: Loader2 },
  pending: { label: 'Queued', tone: 'text-text-secondary', Icon: Loader2 },
  processing: { label: 'Reading', tone: 'text-text-secondary', Icon: Loader2 },
  completed: { label: 'Ready', tone: 'text-success', Icon: CheckCircle2 },
  failed: { label: 'Failed', tone: 'text-error', Icon: AlertCircle },
}

export function SourceItem({ document }: SourceItemProps) {
  const typeLabel = TYPE_LABEL[document.type] ?? document.type.toUpperCase()
  const status = STATUS_TONE[document.status]
  const Icon = status.Icon
  const inflight =
    document.status === 'uploading' ||
    document.status === 'pending' ||
    document.status === 'processing'

  return (
    <li
      data-testid={`source-item-${document.id}`}
      className="flex items-center gap-3 px-4 py-3 border-b border-border-default last:border-b-0"
    >
      <div className="flex items-center justify-center w-9 h-9 shrink-0 rounded-md bg-bg-elevated text-text-tertiary text-xs font-mono uppercase">
        {typeLabel}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-text-primary truncate">
          {document.name}
        </div>
        <div className="text-xs text-text-tertiary truncate">
          {document.extractedData?.category ?? 'unsorted'} · uploaded{' '}
          {formatRelativeFine(document.uploadedAt)}
        </div>
      </div>
      <span className={cn('flex items-center gap-1.5 text-xs', status.tone)}>
        {Icon && <Icon size={12} className={cn(inflight && 'animate-spin')} />}
        {status.label}
      </span>
    </li>
  )
}
